import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef, sizes } from '@shared/port-v1.0.0'

export type Props = BaseReactProps & {
	type: 'string' | 'number'
	stringValue?: string
	numberValue?: number
	fw?: number
	variant: 'text' | 'gradient'
	truncateProp: 'disabled' | 'end' | 'start'
	whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line' | 'break-spaces'
}

import Comp from '../component/Text'

export default {
	module: { static: Comp },
	inNode: {
		inputs: [
			getPortDef({
				name: 'type',
				displayName: 'Type',
				group: 'Data',
				type: [
					{ label: 'String', value: 'string' },
					{ label: 'Number', value: 'number' },
				],
				default: 'string',
			}),
			getPortDef({
				name: 'stringValue',
				displayName: 'Value',
				group: 'Data',
				type: 'string',
				multiline: true,
				dependsOn: (p: Props) => p.type === 'string',
			}),
			getPortDef({
				name: 'numberValue',
				displayName: 'Value',
				group: 'Data',
				type: 'number',
				dependsOn: (p: Props) => p.type === 'number',
			}),
			getPortDef({
				name: 'tt',
				displayName: 'Transform',
				group: 'Params',
				type: [
					{ label: 'Disabled', value: 'disabled' },
					{ label: 'Capitalize', value: 'capitalize' },
					{ label: 'Uppercase', value: 'uppercase' },
					{ label: 'Lowercase', value: 'lowercase' },
				],
				default: 'disabled',
			}),
			getPortDef({
				name: 'fz',
				displayName: 'Size',
				group: 'Dimensions',
				type: sizes,
				default: 'md',
			}),
			getPortDef({
				name: 'lh',
				displayName: 'Line height',
				group: 'Dimensions',
				type: [{ label: 'Inline', value: '1' }, ...sizes],
				default: 'md',
			}),
			getPortDef({
				name: 'ta',
				displayName: 'Align',
				group: 'Layout',
				type: [
					{ label: 'Disabled', value: 'disabled' },
					{ label: 'Start', value: 'start' },
					{ label: 'End', value: 'end' },
					{ label: 'Center', value: 'center' },
					{ label: 'Justify', value: 'justify' },
				],
				default: 'disabled',
			}),
			getPortDef({
				name: 'truncateProp',
				displayName: 'Truncate',
				group: 'Layout',
				type: [
					{ label: 'Disabled', value: 'disabled' },
					{ label: 'End', value: 'end' },
					{ label: 'Start', value: 'start' },
				],
				default: 'disabled',
			}),
			getPortDef({
				name: 'lineClamp',
				displayName: 'Line clamp',
				group: 'Layout',
				type: 'number',
			}),
			getPortDef({
				name: 'whiteSpace',
				displayName: 'White space',
				group: 'Layout',
				type: [
					{ label: 'Normal', value: 'normal' },
					{ label: 'Nowrap', value: 'nowrap' },
					{ label: 'Pre', value: 'pre' },
					{ label: 'Pre-wrap', value: 'pre-wrap' },
					{ label: 'Pre-line', value: 'pre-line' },
					{ label: 'Break-spaces', value: 'break-spaces' },
				],
				default: 'normal',
				dependsOn: (p: Props) => p.truncateProp === 'disabled',
			}),
			getPortDef({
				name: 'span',
				displayName: 'Span',
				group: 'Layout',
				type: 'boolean',
				default: false,
			}),
			getPortDef({
				name: 'fw',
				displayName: 'Weight',
				group: 'Styles',
				type: 'number',
				default: 400,
				validate: (p: Props) =>
					p.fw && [100, 200, 300, 400, 500, 600, 700, 800, 900].includes(p.fw)
						? true
						: 'Дупустимые значения: 100, 200, 300, 400, 500, 600, 700, 800, 900',
			}),
			getPortDef({
				name: 'ff',
				displayName: 'Family',
				group: 'Styles',
				type: 'string',
				default: 'IBM Plex Sans',
			}),
			getPortDef({
				name: 'variant',
				displayName: 'Variant',
				group: 'Styles',
				type: [
					{ label: 'Text', value: 'text' },
					{ label: 'Gradient', value: 'gradient' },
				],
				default: 'text',
			}),
			getPortDef({
				name: 'c',
				displayName: 'Color',
				group: 'Styles',
				type: 'string',
				dependsOn: (p: Props) => p.variant === 'text',
			}),
			getPortDef({
				name: 'gradient',
				displayName: 'Gradient',
				group: 'Styles',
				type: 'objectEval',
				dependsOn: (p: Props) => p.variant === 'gradient',
				codeComment: `//(props) => { return { from: 'blue', to: 'cyan', deg: 90 } }`,
			}),
			getPortDef({
				name: 'td',
				displayName: 'Decoration (CSS)',
				group: 'Styles',
				type: 'string',
			}),
		],
	},
	afterNode: {
		getInspectInfo: (p: Props) =>
			p.stringValue || p.numberValue ? [{ type: 'text', value: p.stringValue || p.numberValue }] : [],
	},
} satisfies ReactNodeDef
